import React, { FC } from 'react';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { checkBox, muiCustomSelect, newThemeInput } from '_fields/inputs';
import { lowerCase, words } from '_fields/normalizers';
import ContinueButton from '@components/_shared/Continue/Button';
import { FormPageTopPart, FormPageTopPartBcmShort } from '@components/_shared/FormPageTopPart/FormPageTopPart';
import { connect, useStore } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { Helmet } from 'react-helmet';
import { Button, cn, LoadingTento, Stack, Text, useHasFullLoaderTimeElapsed } from 'shared-components';
import { NaicsField } from '@components/Apply/MoreDetailsOnYourBusiness/NaicsField';
import { AddressField } from '@components/Apply/MoreDetailsOnYourBusiness/AddressField';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { parseCurrency } from '_fields/parsers';
import {
  ApplyWizardSteps,
  STEP_BCM_2_BUSINESS_MORE_DETAILS,
  STEP_BCM_BUSINESS_MORE_DETAILS,
  STEP_BUSINESS_MORE_DETAILS,
  STEP_EQ_LEAD_YOUR_BUSINESS,
} from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { ContinueButtonWide } from '@components/_shared/Continue/ContinueButton2';
import withLeadOnSubmit from '@components/Apply/withOnSubmit/withLeadOnSubmit';
import ensureNotLoggedIn from '../../../hooks/ensureNotLoggedIn';
import { ensureLeadIds } from '../../../hooks/ensureLeadIds';
import { EQFlowFooter } from '../_shared/EQFlowFooter';

type MoreDetailsOnYourBusinessProps = {
  loanType: string;
  handleSubmit: () => void;
  invalid: boolean;
  years?: number;
  saving?: boolean;
};

const MoreDetailsOnYourBusinessWrapper: FC<MoreDetailsOnYourBusinessProps> = (props) => {
  const auth0 = useAuth0();

  if (auth0.isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" fullWidth>
        <Spinner />
      </Stack>
    );
  }

  return <MoreDetailsOnYourBusinessMainComponent {...props} />;
};

const MoreDetailsOnYourBusinessMainComponent: FC<MoreDetailsOnYourBusinessProps> = ({
  handleSubmit,
  invalid,
  saving: savingOnSubmit,
  years,
}) => {
  const { saving } = useApplyWizardContext();
  const form = useForm();

  const store = useStore();
  const latestValues = getFormValues('application')(store.getState() as RootState) || {};
  const validationOutput = reduxFormZodValidate(ApplyWizardSteps[STEP_BUSINESS_MORE_DETAILS].zodSchema)(latestValues);
  const moreYearsValue = years && Number(years) > 20 ? Number(years) : 20; // Not entirely sure why this logic was added before, but I kept it for legacy reasons.

  return (
    <div>
      <Helmet>
        <title>More Details on Your Business</title>
      </Helmet>
      <div className="w-full flex flex-col gap-[20px] mt-[16px]">
        <Text variant="subheadline-sm2" className="!text-[16px] !font-extended py-[20px] !leading-[100%]">
          TELL US ABOUT YOUR BUSINESS
        </Text>
        <Text variant="subheadline-sm2" className="!text-[20px] !font-standard !leading-[30px]">
          Your Business Information
        </Text>
        <Form {...form}>
          <form onSubmit={handleSubmit}>
            <Stack gapPx="40px">
              <Stack gapPx="5px" justifyItems="center" templateColumns="1fr" fullWidth>
                <Field
                  autoFocus
                  name="business_name"
                  variant="transparent-black"
                  component={newThemeInput}
                  type="text"
                  placeholder="Company Name*"
                  normalize={words}
                  label="Company Name*"
                  data-testid="business_name"
                  className="!max-w-[592px]"
                />

                <Field
                  data-testid="business-website"
                  name="business_website"
                  variant="transparent-black"
                  component={newThemeInput}
                  type="text"
                  placeholder="Business Website"
                  normalize={lowerCase}
                  format={(v: string) => v?.trim()}
                  className="!max-w-[592px]"
                />
                <AddressField
                  variant="transparent-black"
                  autoFocus={false}
                  getManualAddressText={(isManualAddress: boolean) => {
                    return isManualAddress ? 'Use Search Tool' : 'Enter Manually';
                  }}
                  toggleManualAddressClassName="uppercase"
                  geoSuggestClassName="!max-w-[592px]"
                  geoSuggestInputClassName="!max-w-[592px]"
                  addressContainerProps={{
                    className: 'w-full !max-w-[592px]',
                  }}
                  addressColumnContainerProps={{
                    className: '!max-w-[592px]',
                  }}
                  addressStreetProps={{
                    className: '!max-w-[592px]',
                  }}
                  addressSuiteProps={{
                    className: '!max-w-[592px]',
                  }}
                  addressCityProps={{
                    className: '!max-w-[592px]',
                  }}
                  addressStateProps={{
                    className: '!max-w-[592px]',
                    selectTriggerProps: {
                      className: '!max-w-[592px]',
                    },
                  }}
                  addressPostalCodeProps={{
                    className: '!max-w-[592px]',
                  }}
                  showDefaultManualAddress={false}
                />
                {(latestValues as any).business_city && (validationOutput as any).business_street && (
                  <Text variant="error">Please enter a valid street address</Text>
                )}

                <Stack
                  flow="column"
                  templateColumns="1fr 1fr"
                  gapVariant="input-to-input"
                  alignItems="start"
                  className={cn('gap-[10px] w-full max-w-[592px]')}
                >
                  <Field
                    name="business_years"
                    variant="transparent-black"
                    component={muiCustomSelect}
                    label="Years in business*"
                    className="tib-select !max-w-[592px]"
                    autoFocus
                    data-testid="business_years"
                    selectValueProps={{ placeholder: 'Years in Business*' }}
                    valueAsNumber
                    options={[
                      { value: 0, text: 'under 1 year' },
                      { value: 1, text: '1 year' },
                      { value: 2, text: '2 years' },
                      { value: 3, text: '3 years' },
                      { value: 4, text: '4 years' },
                      { value: 5, text: '5 years' },
                      { value: 6, text: '6 years' },
                      { value: 7, text: '7 years' },
                      { value: 8, text: '8 years' },
                      { value: 9, text: '9 years' },
                      { value: 10, text: '10 years' },
                      { value: 11, text: '11 years' },
                      { value: 12, text: '12 years' },
                      { value: 13, text: '13 years' },
                      { value: 14, text: '14 years' },
                      { value: 15, text: '15 years' },
                      { value: 16, text: '16 years' },
                      { value: 17, text: '17 years' },
                      { value: 18, text: '18 years' },
                      { value: 19, text: '19 years' },
                      { value: moreYearsValue, text: '20+ years' },
                    ]}
                  />

                  <Field
                    name="business_revenue"
                    variant="transparent-black"
                    component={newThemeInput}
                    labelText="Annual Gross Revenue*"
                    parse={parseCurrency}
                    formatAsNumber
                    labelClassName="infoFormCustomLabel"
                    data-testid="business_revenue"
                  />
                </Stack>

                <Field
                  data-testid="fed-tax-id"
                  variant="transparent-black"
                  name="business_ein"
                  component={newThemeInput}
                  type="tel"
                  label="Tax ID (Optional)"
                  placeholder="Federal Tax ID* (XX-XXXXXXX)"
                  formatAsFederalTaxId
                  className="!max-w-[592px]"
                />
              </Stack>
              <div className="w-full flex flex-col items-center justify-center gap-[10px] mx-auto">
                <Button
                  variant="primary-green"
                  color="primary"
                  type="submit"
                  disabled={invalid || saving}
                  isLoading={saving || savingOnSubmit}
                  data-testid="info_continue"
                  className="!max-w-[592px] !h-[50px] border-[1px] border-spark2 !py-[12.5px] font-condensed leading-[120%] px-[50px] !text-[16px]"
                >
                  CONTINUE
                </Button>
                <Text variant="body-form-label" className="!text-[16px] !leading-[18px] !font-condensed text-charcoal2">
                  *NO HARD CREDIT CHECKS
                </Text>
              </div>
            </Stack>
          </form>

          <EQFlowFooter />
        </Form>
      </div>
    </div>
  );
};

const MoreDetailsOnYourBusinessWrapper2 = reduxForm<MoreDetailsOnYourBusinessProps, MoreDetailsOnYourBusinessProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_YOUR_BUSINESS].zodSchema),
})(MoreDetailsOnYourBusinessWrapper);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    years: appSelector(state, 'business_years'),
  };
};

export const EQLeadYourBusinessStep = ensureLeadIds(
  ensureNotLoggedIn(
    withLeadOnSubmit(connect(mapStateToProps)(MoreDetailsOnYourBusinessWrapper2), STEP_EQ_LEAD_YOUR_BUSINESS)
  )
);
